import {
    Box,
    Divider,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ArrayInput,
    AutocompleteInput,
    BooleanInput,
    DateTimeInput,
    NumberInput,
    ReferenceInput,
    required,
    SelectArrayInput,
    SelectInput,
    SimpleFormIterator,
    TextInput,
    useGetList,
    useRecordContext,
} from 'react-admin';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { MissionContactReferenceInput } from '@app/mission/edit/MissionContactReferenceInput';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import type { AntennaId } from '@wwf/sdk';
import { RichTextInput } from 'ra-input-rich-text';
import { AddressAutofillInputs } from '@app/misc/AddressAutofillInputs';
import { useMissionTypes } from '@app/mission_type/useMissionType';
import { MissionTypeLightCard } from '@app/mission_type/MissionTypeLightCard';
import { MissionVisibilityLightCard } from '@app/mission/MissionVisibilityLightCard';
import { AnimationLightCard } from '@app/animation/show/AnimationLightCard';
import { UrlInput } from '@app/misc/UrlInput';
import { MissionInput } from '@domain/mission';
import { memberTypeToDefaultMissionVisibility } from '@domain/missionVisibility';
import { format } from 'date-fns';
import {
    MissionSessionEditionInput,
    computeMissionPeriodFromSessions,
} from '@wwf/sdk';
import { antennaOptionText } from '@app/antenna/AntennaInputOption';

const validateRequired = required();

/**
 * Formats a Date to an ISO string with the local timezone offset (e.g. 2025-02-13T10:00:00+01:00).
 * Postgres timestamp with time zone stores the instant correctly and the timezone is preserved in the value.
 */
const toISOStringWithTimezone = (date: Date): string => {
    const offset = -date.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const absOffset = Math.abs(offset);
    const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
    const minutes = String(absOffset % 60).padStart(2, '0');
    const offsetStr = `${sign}${hours}:${minutes}`;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${d}T${h}:${min}:${s}${offsetStr}`;
};

/**
 * Parses the datetime-local value (local time) to an ISO string with timezone offset.
 * The DB receives e.g. 2025-02-13T10:00:00+01:00 so the date and timezone are stored explicitly.
 */
const parseDateTimeToIsoWithTimezone = (
    value: string | Date | null | undefined
): string | null => {
    if (value == null || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : toISOStringWithTimezone(date);
};

const MissionSessionsEditor = () => {
    const antennaId = useWatch({ name: 'antennaId' }) as AntennaId | undefined;
    return (
        <Stack gap={1}>
            <Typography variant="h6">Sessions</Typography>

            <ArrayInput
                source="sessions"
                label={false}
                defaultValue={[{ sortOrder: 0 }]}
            >
                <SimpleFormIterator inline>
                    <TextInput source="id" sx={{ display: 'none' }} />
                    <TextInput
                        source="label"
                        label="Label"
                        helperText={false}
                    />
                    <DateTimeInput
                        source="startDate"
                        label="Start"
                        helperText={false}
                        parse={parseDateTimeToIsoWithTimezone}
                    />
                    <DateTimeInput
                        source="endDate"
                        label="End"
                        helperText={false}
                        parse={parseDateTimeToIsoWithTimezone}
                    />
                    <MissionContactReferenceInput
                        source="contactId"
                        antennaId={antennaId}
                        label="Session contact"
                        helperText="Optional; defaults to mission contact"
                    />
                </SimpleFormIterator>
            </ArrayInput>
        </Stack>
    );
};

const MissionComputedPeriodReadonly = () => {
    const sessions = useWatch({ name: 'sessions' }) as
        | MissionSessionEditionInput[]
        | undefined;
    const { startDate, endDate } = computeMissionPeriodFromSessions(sessions);
    const fmt = (v: unknown) => {
        if (v == null || v === '') return '—';
        try {
            return format(new Date(v as string), 'PPp');
        } catch {
            return String(v);
        }
    };
    return (
        <Stack gap={0.5}>
            <Typography variant="h6">Mission period</Typography>
            <Typography variant="body2">
                {fmt(startDate)} → {fmt(endDate)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Computed as the earliest session start and latest session end.
            </Typography>
        </Stack>
    );
};

export const MissionInputs = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    return (
        <Stack gap={4} p={1}>
            <MissionInfoInputs />
            <Divider orientation={'horizontal'} flexItem />

            <Stack gap={4} flexDirection={isMobile ? 'column' : 'row'}>
                <Stack gap={4} flexDirection={'column'} flex={5}>
                    <MissionLinkedToInputs />
                    <Divider orientation={'horizontal'} flexItem />
                    <MissionLocationInputs />
                </Stack>
                <Divider
                    orientation={isMobile ? 'horizontal' : 'vertical'}
                    flexItem
                />
                <Stack gap={4} flexDirection={'column'} flex={2}>
                    <MissionMiscInputs />
                </Stack>
            </Stack>
            <Divider orientation={'horizontal'} flexItem />
            <MissionComputedPeriodReadonly />

            <MissionSessionsEditor />
        </Stack>
    );
};

const MissionInfoInputs = () => {
    return (
        <Stack gap={1}>
            <TextInput
                source="name"
                label="Mission name"
                validate={validateRequired}
                helperText={false}
            />
            <RichTextInput source="description" helperText={false} />
        </Stack>
    );
};

const MissionLocationInputs = () => {
    return (
        <Stack gap={1}>
            <Typography variant="h6">Location</Typography>
            <AddressAutofillInputs />
            <TextInput
                source="partnerInfo"
                label="Partner info"
                multiline
                rows={2}
                helperText={false}
            />
        </Stack>
    );
};

const MissionLinkedToInputs = () => {
    const antennaId = useWatch({ name: 'antennaId' }) as AntennaId | undefined;

    return (
        <Stack gap={1}>
            <Typography variant="h6">Linked to</Typography>
            <ReferenceInput
                source="antennaId"
                reference="antenna"
                sort={{ field: 'name', order: 'ASC' }}
            >
                <SelectInput
                    optionText={antennaOptionText}
                    validate={validateRequired}
                    helperText={false}
                />
            </ReferenceInput>

            <MissionContactReferenceInput antennaId={antennaId} />

            <NumberInput
                source="participantLimit"
                defaultValue={0}
                helperText="Max. participants per session, including the session contact (0 = unlimited)"
                min={0}
                max={100}
            />
        </Stack>
    );
};

const MissionMiscInputs = () => {
    const record = useRecordContext<MissionInput>();
    const { setValue } = useFormContext();
    const selectedTypeId = useWatch({ name: 'type', defaultValue: '' });
    const antennaId = useWatch({ name: 'antennaId' }) as AntennaId | undefined;
    const { missionVisibilityLabels } = useConfigurationContext();
    const { data: missionTypes = [] } = useMissionTypes();

    const { data: animations = [] } = useGetList('animation', {
        pagination: { page: 0, perPage: 10000 },
        sort: { field: 'name', order: 'ASC' },
        filter: {
            enabled: true,
            ...(selectedTypeId ? { missionType: selectedTypeId } : {}),
        },
        meta: { antennaId },
    });

    const savedWhoCanSee = record?.whoCanSee ?? null;
    const savedWhoCanParticipate = record?.whoCanParticipate ?? null;
    const savedTypeId = record?.type;

    // Sync "Who can see/participate" with the mission type only for new missions (no saved
    // visibility) or when the user changes the mission type in the form.
    useEffect(() => {
        if (!selectedTypeId) return;

        const visibilityUnset =
            savedWhoCanSee == null && savedWhoCanParticipate == null;
        const userChangedMissionType =
            savedTypeId != null && selectedTypeId !== savedTypeId;

        if (!visibilityUnset && !userChangedMissionType) return;

        const missionType = missionTypes.find(t => t.id === selectedTypeId);
        if (missionType?.whoCanSee) {
            setValue(
                'whoCanSee',
                memberTypeToDefaultMissionVisibility(
                    missionType.whoCanSee,
                    true
                )
            );
        }
        if (missionType?.whoCanParticipate) {
            setValue(
                'whoCanParticipate',
                memberTypeToDefaultMissionVisibility(
                    missionType.whoCanParticipate,
                    false
                )
            );
        }
    }, [
        selectedTypeId,
        missionTypes,
        setValue,
        savedWhoCanSee,
        savedWhoCanParticipate,
        savedTypeId,
    ]);

    return (
        <Stack gap={1}>
            <Typography variant="h6">Visibility</Typography>

            <SelectInput
                source="type"
                label="Type"
                choices={missionTypes}
                optionText={<MissionTypeLightCard />}
                validate={[validateRequired]}
                helperText={false}
            />

            <SelectInput
                source="whoCanSee"
                label="Who can see"
                choices={missionVisibilityLabels.map(entry => ({
                    id: entry.value,
                    name: entry.label,
                    icon: entry.icon,
                }))}
                optionText={<MissionVisibilityLightCard />}
                validate={validateRequired}
                defaultValue="VOLUNTEER_CLOSE"
                helperText={
                    <>
                        Visible but won't be able to register for the mission.
                        <br />
                        Private (link / closed) limits visibility to
                        participants or members with the direct link.
                        <br />
                        Staff can always read missions (for administration),
                        regardless of audience.
                    </>
                }
            />

            <SelectInput
                source="whoCanParticipate"
                label="Who can participate"
                choices={missionVisibilityLabels.map(entry => ({
                    id: entry.value,
                    name: entry.label,
                    icon: entry.icon,
                }))}
                optionText={<MissionVisibilityLightCard />}
                validate={validateRequired}
                defaultValue="VOLUNTEER_CLOSE"
                helperText={
                    'Who can register for the mission. Staff can always register for missions.'
                }
            />

            <Typography variant="h6">Work</Typography>

            <Box
                sx={{
                    // Ra chips are small + ellipsis; AnimationLightCard needs a taller label.
                    '& .RaSelectArrayInput-chip': {
                        height: 'auto',
                        minHeight: theme => theme.spacing(3),
                        maxWidth: '100%',
                        whiteSpace: 'normal',
                    },
                    '& .RaSelectArrayInput-chip .MuiChip-label': {
                        overflow: 'visible',
                        textOverflow: 'clip',
                        whiteSpace: 'normal',
                        display: 'flex',
                        alignItems: 'center',
                    },
                }}
            >
                <SelectArrayInput
                    source="animations"
                    label="Animations"
                    choices={animations}
                    optionText={<AnimationLightCard noLink />}
                    helperText={
                        antennaId
                            ? 'Animations for this antenna’s organization. Gray = not in stock at this antenna. Only enabled animations matching the mission type are listed.'
                            : 'Select an antenna to list animations and stock availability.'
                    }
                />
            </Box>

            <ReferenceInput
                source="studyFormId"
                reference="studyForm"
                filter={{
                    enabled: true,
                    ...(selectedTypeId
                        ? { missionTypeId: selectedTypeId }
                        : {}),
                }}
            >
                <AutocompleteInput
                    label="Study form"
                    optionText="title"
                    optionValue="id"
                    filterToQuery={search => ({ search })}
                    helperText="Only enabled forms matching the selected mission type are listed."
                />
            </ReferenceInput>

            <UrlInput
                source="visioConferenceLink"
                label="Visio conference link"
                fullWidth
                helperText={false}
            />
            <BooleanInput
                source="withSales"
                label="With sales"
                helperText={false}
                defaultValue={false}
            />
        </Stack>
    );
};
